import { Dispatch } from 'redux';
import { NavigateFunction } from 'react-router-dom';
import { CancelFunctions } from 'components/common/types';
import { zonedTimeToUtc } from 'date-fns-tz';
import { getDefaultSweep } from 'utils/deal';
import { getCampaignImpressionMetrics, getDeal } from 'modules/api/programmaticDeal';
import { isCanceledError } from 'modules/api/request';

import { transformDealLineDetails } from 'components/pages/DealWithLines/DealLines/transformDealDetails';
import { fetchDealBids } from 'modules/api/deal';

import {
  Availability,
  CommonDeal,
  DealPriority,
  DealStatus,
  DealType,
  FrontEndType,
  Programmatic,
  SelectedDay,
} from 'components/common/types/Deal.types';
import { Store } from 'components/common/types/Store.types';
import { BackupFormData, Loading } from 'store/dealManagement/types';
import {
  changeCurrentDealLineAvailability,
  changeDealCurrentLineData,
  changeDealData,
  changeDealDetails,
  changeFormParams,
  clearDisclaimer,
  clearForm,
  resetDealLineAvailability,
  startLoading,
  stopLoading,
  updateCurrentLineAndBackupLineProperty,
  updatePlannerSettings,
} from 'store/dealManagement/reducer';
import {
  fetchOpenStreetMapPois,
  fetchPlannerPois,
} from 'components/pages/Planner/PlannerSections/DataTargetingContent/PointsOfInterest/utils';
import { getIsReseller } from 'store/publisher/selectors';
import { getUnderlyingMediaOwnerRegions } from 'store/reseller/selectors';
import { isEmpty } from 'lodash';
import { addToastNotification, clearNotification } from '../notification/reducer';
import { getIsValidAvailabilityRequest } from './selectors';

export const changeDealType =
  (dealType: DealType, hasNonGuaranteedExtendedTargetEnabled: boolean) =>
  async (dispatch: Dispatch): Promise<void> => {
    const isGuaranteed = dealType === DealType.GUARANTEED;

    dispatch(
      changeDealCurrentLineData({
        sweep: getDefaultSweep(dealType, hasNonGuaranteedExtendedTargetEnabled),
      }),
    );

    dispatch(
      changeDealData({
        dealType,
        dealPriority: isGuaranteed ? DealPriority.PREFERRED : DealPriority.STANDARD,
      }),
    );
  };

export const changeSelectedDays =
  (selectedDays: { [key: string]: SelectedDay }) =>
  async (dispatch: Dispatch): Promise<void> => {
    dispatch(changeDealCurrentLineData({ selectedDays }));
  };

export const fetchAndUpdatePoiPoints =
  (cancelFunctions: CancelFunctions, isClientSideAssetFiltering?: boolean) =>
  async (dispatch: Dispatch, getState: () => Store): Promise<void> => {
    dispatch(startLoading(Loading.GET_POI_DATA));

    const { plannerPoi, openStreetMapPoi } = getState().dealManagement.commonDeal.currentLine.proximity;
    const { openStreetMapCountryName } = getState().publisher.configuration;
    const isReseller = getIsReseller(getState());
    const resellerMediaOwnerRegions = getUnderlyingMediaOwnerRegions(getState());
    const openStreetMapCountryNames = isReseller ? resellerMediaOwnerRegions : [openStreetMapCountryName];

    try {
      const fetchedAndTransformedPois = await Promise.allSettled([
        fetchPlannerPois({ pois: plannerPoi, cancelFunctions }),
        fetchOpenStreetMapPois({ pois: openStreetMapPoi, openStreetMapCountryNames }),
      ]);

      const fetchedAndTransformedPlannerPois =
        fetchedAndTransformedPois[0].status === 'fulfilled' ? fetchedAndTransformedPois[0].value : [];
      const fetchedAndTransformedOpenStreetMapPois =
        fetchedAndTransformedPois[1].status === 'fulfilled' ? fetchedAndTransformedPois[1].value : [];

      dispatch(updatePlannerSettings({ isClientSideAssetFiltering }));
      dispatch(
        changeDealCurrentLineData({
          proximity: {
            plannerPoi: fetchedAndTransformedPlannerPois,
            openStreetMapPoi: fetchedAndTransformedOpenStreetMapPois,
          },
        }),
      );
      // eslint-disable-next-line no-empty
    } catch (error) {
    } finally {
      dispatch(stopLoading(Loading.GET_POI_DATA));
    }
  };

export const getDealDetails =
  (
    internalId: string,
    cancelFunctions: CancelFunctions,
    navigate: NavigateFunction,
    hasAdsDealLevelCPMEnabled: boolean,
    hasNonGuaranteedExtendedTargetEnabled: boolean,
    preferredLineId?: string,
  ) =>
  // eslint-disable-next-line sonarjs/cognitive-complexity
  async (dispatch: Dispatch, getState: () => Store): Promise<void> => {
    dispatch(startLoading(Loading.GET_DEAL_DETAILS));
    try {
      const dealDetails = await getDeal(internalId, cancelFunctions);

      try {
        const bids = await fetchDealBids(cancelFunctions, internalId);

        if (bids) {
          dealDetails.bids = bids;
        }
      } catch {} // eslint-disable-line no-empty

      if (
        dealDetails.campaignImpressions.allocatedImpressions !== null &&
        dealDetails.dealTypeCode === DealType.GUARANTEED &&
        dealDetails.dealLines[0].frontEndType === FrontEndType.STANDARD &&
        [DealStatus.LIVE, DealStatus.ENDED, DealStatus.TERMINATED].includes(dealDetails.bookingStatusCode)
      ) {
        try {
          const state = getState();
          const timezoneCode = state.header.timezone?.code || state.publisher.configuration.timezone;
          const campaignImpressionMetrics = await getCampaignImpressionMetrics(
            cancelFunctions,
            dealDetails.campaignID,
            timezoneCode ? zonedTimeToUtc(dealDetails.startDate, timezoneCode).toISOString() : dealDetails.startDate,
            timezoneCode ? zonedTimeToUtc(dealDetails.endDate, timezoneCode).toISOString() : dealDetails.endDate,
          );
          if (campaignImpressionMetrics) {
            dealDetails.impressionMetrics = campaignImpressionMetrics;
          }
        } catch {} // eslint-disable-line no-empty
      }

      const transformedDealDetails = transformDealLineDetails({
        ...dealDetails,
        hasNonGuaranteedExtendedTargetEnabled,
      }) as {
        backupFormData: BackupFormData;
        commonDeal: Partial<CommonDeal>;
        programmatic: Programmatic;
      };

      dispatch(
        changeDealDetails({
          ...transformedDealDetails,
          hasAdsDealLevelCPMEnabled,
          preferredLineId,
        }),
      );

      const { currentLine } = transformedDealDetails.commonDeal;
      const isClientSideAssetFiltering =
        !!currentLine?.proximity.plannerPoi.length ||
        !!currentLine?.proximity.openStreetMapPoi.length ||
        !isEmpty(currentLine?.segment?.selectedSegments);

      await dispatch(fetchAndUpdatePoiPoints(cancelFunctions, isClientSideAssetFiltering));
    } catch (error) {
      if (isCanceledError(error)) return;
      dispatch(clearForm());
      navigate('/deal');
    } finally {
      dispatch(stopLoading(Loading.GET_DEAL_DETAILS));
    }
  };

export const handleFetchAvailability =
  (
    handleAvailability: (dealFormData: FormData, cancelFunctions: CancelFunctions) => Promise<Availability>,
    dealFormData: FormData,
    cancelFunctions: CancelFunctions,
  ): ((dispatch: Dispatch, getState: () => Store) => Promise<void>) =>
  async (dispatch: Dispatch, getState: () => Store): Promise<void> => {
    const isValidAvailabilityRequest = getIsValidAvailabilityRequest(getState());
    const { commonDeal } = getState().dealManagement;

    if (!isValidAvailabilityRequest) {
      cancelFunctions.CHECK_AVAILABILITY?.();
      dispatch(resetDealLineAvailability());
      return;
    }

    dispatch(clearNotification());
    dispatch(clearDisclaimer());
    dispatch(changeFormParams({ isForecastedAllocation: false }));
    dispatch(changeDealCurrentLineData({ isCurrentLineWithProposalAllocation: false }));
    dispatch(
      updateCurrentLineAndBackupLineProperty({
        lineId: commonDeal.currentLine.id,
        type: 'isFetchingAvailability',
        value: true,
      }),
    );

    try {
      const data = await handleAvailability(dealFormData, cancelFunctions);

      if (!data.assets.length) {
        const message =
          'There are no frames available for the targeting criteria you have chosen. Please revise your targeting selection.';

        dispatch(
          addToastNotification({
            description: message,
            timeout: 10000,
            type: 'Warning',
          }),
        );

        throw new Error(message);
      }
      dispatch(changeCurrentDealLineAvailability(data));
      dispatch(
        updateCurrentLineAndBackupLineProperty({
          lineId: commonDeal.currentLine.id,
          type: 'isFetchingAvailability',
          value: false,
        }),
      );
    } catch (error) {
      if (error.code !== 'ERR_CANCELED') {
        dispatch(
          updateCurrentLineAndBackupLineProperty({
            lineId: commonDeal.currentLine.id,
            type: 'isFetchingAvailability',
            value: false,
          }),
        );
      }
      dispatch(resetDealLineAvailability());
    }
  };
